import React, { useRef, useState } from "react";
import { useRebaseClient } from "@rebasepro/app";
import type { RebaseClient } from "@rebasepro/types";
import type { CollectionActionsProps } from "@rebasepro/cms-types";
import {
    Alert as RebaseAlert,
    Button as RebaseButton,
    Chip as RebaseChip,
    CircularProgress as RebaseCircularProgress,
    Dialog as RebaseDialog,
    DialogActions as RebaseDialogActions,
    DialogContent as RebaseDialogContent,
    DialogTitle as RebaseDialogTitle,
    Typography as RebaseTypography,
    cls,
    defaultBorderMixin
} from "@rebasepro/ui";
import { FileUp } from "lucide-react";

const Alert = RebaseAlert as React.ComponentType<any>;
const Button = RebaseButton as React.ComponentType<any>;
const Chip = RebaseChip as React.ComponentType<any>;
const CircularProgress = RebaseCircularProgress as React.ComponentType<any>;
const Dialog = RebaseDialog as React.ComponentType<any>;
const DialogActions = RebaseDialogActions as React.ComponentType<any>;
const DialogContent = RebaseDialogContent as React.ComponentType<any>;
const DialogTitle = RebaseDialogTitle as React.ComponentType<any>;
const Typography = RebaseTypography as React.ComponentType<any>;
const UploadIcon = FileUp as React.ComponentType<any>;

/** A CV is a few hundred KB. Checked here too so an obvious miss costs no upload. */
const MAX_BYTES = 12 * 1024 * 1024;

/**
 * What the backend is doing while the admin waits, with the second each stage
 * typically starts at. Estimates driven by a timer, not progress reported by
 * the server — `extract-cv` is one opaque POST — but a wait with no caption
 * reads as a hang. Same reasoning as `ExtractJobAction`.
 */
const PHASES = [
    { after: 0, label: "Subiendo el PDF…" },
    { after: 3, label: "Leyendo el CV con IA…" },
    { after: 25, label: "Casi listo…" }
];

/**
 * A short unique segment for the upload path.
 *
 * `crypto.randomUUID` needs a secure context, which the admin always is in
 * practice but is not worth crashing an import over; the fallback only has to
 * avoid collisions between two imports, not be unguessable — the object is
 * admin-only by path either way.
 */
function cryptoRandomId(): string {
    const c = globalThis.crypto as Crypto | undefined;
    if (c?.randomUUID) return c.randomUUID();
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Read a File as bare base64 (no `data:` prefix), which is what the API wants. */
function toBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("No se pudo leer el archivo"));
        reader.onload = () => {
            const result = String(reader.result ?? "");
            const comma = result.indexOf(",");
            resolve(comma >= 0 ? result.slice(comma + 1) : result);
        };
        reader.readAsDataURL(file);
    });
}

/** What the extraction found, so the admin can judge it before the form opens. */
function Summary({ values }: { values: Record<string, any> }) {
    const q = (values.questionnaire ?? {}) as Record<string, any>;
    const chips = [
        ...(Array.isArray(q.degree) ? q.degree : []),
        ...(Array.isArray(q.certifications) ? q.certifications : []),
        ...(Array.isArray(q.skills) ? q.skills : [])
    ].slice(0, 8);

    return (
        <div className={cls("flex flex-col gap-2 p-3 border rounded-lg", defaultBorderMixin)}>
            <Typography variant="subtitle2">{values.full_name}</Typography>
            <Typography variant="body2" color="secondary">
                {[values.email, values.phone, values.location].filter(Boolean).join(" · ") || "sin datos de contacto"}
            </Typography>
            {chips.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {chips.map((v: string, i: number) => (
                        <Chip key={`${v}-${i}`} size="small" colorScheme="grayLighter">{v}</Chip>
                    ))}
                </div>
            )}
            {values.professional_objective && (
                <Typography variant="body2" className="opacity-80">
                    {String(values.professional_objective).slice(0, 220)}
                </Typography>
            )}
        </div>
    );
}

/**
 * Create a candidate from their CV.
 *
 * The extraction opens the ordinary new-talent form pre-filled rather than
 * writing a row: what comes back is a model's reading of somebody's
 * professional record, and the person who has to stand behind it should see it
 * first. Nothing is saved until they press save, exactly as if they had typed
 * it — which also means every field stays editable and the usual validation
 * applies.
 */
export function ImportCvAction<M extends Record<string, unknown>>({
    openNewDocument
}: CollectionActionsProps<M>) {
    const client = useRebaseClient<RebaseClient>();
    const inputRef = useRef<HTMLInputElement>(null);

    const [open, setOpen] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [error, setError] = useState("");
    const [extracted, setExtracted] = useState<Record<string, any> | null>(null);
    // The key the PDF was stored under, so the created candidate keeps the
    // document it was built from. Null when the upload failed — see `extract`.
    const [storedKey, setStoredKey] = useState<string | null>(null);

    React.useEffect(() => {
        if (!loading) { setElapsed(0); return; }
        const started = Date.now();
        const timer = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
        return () => clearInterval(timer);
    }, [loading]);

    const phaseLabel = [...PHASES].reverse().find(p => elapsed >= p.after)?.label ?? PHASES[0].label;

    const reset = () => {
        setFile(null);
        setError("");
        setExtracted(null);
        setStoredKey(null);
        setLoading(false);
        if (inputRef.current) inputRef.current.value = "";
    };

    const pick = (chosen: File | null) => {
        setError("");
        setExtracted(null);
        if (!chosen) { setFile(null); return; }
        if (chosen.type !== "application/pdf") {
            setFile(null);
            setError("Sube el CV en PDF.");
            return;
        }
        if (chosen.size > MAX_BYTES) {
            setFile(null);
            setError("El PDF es demasiado grande (máx. 12 MB).");
            return;
        }
        setFile(chosen);
    };

    const extract = async () => {
        if (!file || !client?.functions) return;
        setLoading(true);
        setError("");
        try {
            const data = await toBase64(file);
            const res = await client.functions.invoke<{
                ok: boolean;
                values: Record<string, any>;
            }>("extract-cv", { filename: file.name, mimeType: file.type, data });

            if (!res?.ok || !res.values) throw new Error("No pudimos leer este CV. Prueba con otro archivo o crea la ficha a mano.");

            // Keep the document, not just what was read out of it. Reading a
            // CV and discarding it leaves a record nobody can check against
            // the source — and this is the one moment the file is in hand,
            // because it never had a URL to point at.
            //
            // Uploaded under `cv-imports/`, which is a real path rather than
            // the collection's `storagePath`: that one keys off the candidate,
            // and there is no candidate yet — the row is created only if the
            // admin saves the form this opens. `storageAuthorize` splits the
            // key and grants the first segment's owner or an admin, and
            // `cv-imports` matches no `user.uid`, so the file is admin-only,
            // which is correct for a document its subject never uploaded.
            //
            // A failed upload must not lose the extraction: the admin can
            // still create the candidate and attach the PDF by hand, so this
            // reports and carries on rather than throwing.
            let key: string | null = null;
            try {
                const stored = await (client as any).storage.putObject({
                    file,
                    key: `cv-imports/${cryptoRandomId()}/${file.name}`,
                    public: false
                });
                key = stored?.key ?? null;
            } catch (uploadError: any) {
                console.warn("CV uploaded failed, continuing with the extraction:", uploadError);
            }
            setStoredKey(key);
            setExtracted(res.values);
        } catch (err: any) {
            setError(err?.message || "No se pudo leer el CV.");
        } finally {
            setLoading(false);
        }
    };

    const openForm = () => {
        if (!extracted) return;
        // Stamped here rather than by the backend, because this is the only
        // path that produces one: a record built from a document, with nobody
        // having registered, agreed to anything or answered a question. The
        // list needs to say so — someone contacted as though they had applied,
        // when they never did, is the failure this marks.
        const values = {
            ...extracted,
            origin: "cv_import",
            // Only when it actually stored. A `cv_url` pointing at a key that
            // does not exist renders as a broken attachment, which reads as
            // "the CV is lost" rather than "it was never uploaded".
            ...(storedKey ? { cv_url: storedKey } : {})
        };
        reset();
        setOpen(false);
        openNewDocument(values as unknown as Partial<M>);
    };

    return (
        <>
            {/* Labelled, not a bare icon — the same lesson as "Importar desde
                URL", which sat unused for three months as an unnamed glyph. */}
            <Button onClick={() => setOpen(true)} variant="text" color="primary">
                <UploadIcon size={18}/>
                Importar CV
            </Button>

            <Dialog
                open={open}
                onOpenChange={(next: boolean) => { if (!next) reset(); setOpen(next); }}
                maxWidth="2xl"
                fullWidth
            >
                <DialogTitle variant="h6" className="mb-2">Crear candidato desde un CV</DialogTitle>
                <DialogContent>
                    <div className="flex flex-col gap-4 mt-1">
                        <Typography variant="body2" color="secondary">
                            Sube el CV en PDF. Se abrirá el formulario con lo que se haya podido
                            leer, para revisarlo antes de guardar.
                        </Typography>

                        <input
                            ref={inputRef}
                            type="file"
                            accept="application/pdf"
                            disabled={loading}
                            onChange={(e) => pick(e.target.files?.[0] ?? null)}
                            className={cls(
                                "block w-full text-sm p-3 border rounded-lg",
                                "file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0",
                                "file:text-sm file:bg-surface-raised",
                                defaultBorderMixin
                            )}
                        />

                        {error && <Alert color="error">{error}</Alert>}

                        {loading && (
                            <div className={cls("flex items-center gap-3 text-sm p-3 border rounded-lg", defaultBorderMixin)}>
                                <CircularProgress size="smallest"/>
                                <div className="flex flex-col gap-1">
                                    <span>{phaseLabel}</span>
                                    <span className="opacity-70">{elapsed}s · no se ha creado nada todavía</span>
                                </div>
                            </div>
                        )}

                        {extracted && (
                            <>
                                <Alert color="info">
                                    Revisa los datos antes de guardar. Lo que no aparecía en el CV
                                    se ha dejado vacío a propósito.
                                </Alert>
                                {/* Said plainly either way. The PDF is the only
                                    copy — it never had a URL to fall back on —
                                    so "did it keep it?" is a question the admin
                                    should not have to open the record to
                                    answer. */}
                                {storedKey
                                    ? (
                                        <Typography variant="body2" color="secondary">
                                            El PDF se guardará adjunto a la ficha.
                                        </Typography>
                                    )
                                    : (
                                        <Alert color="warning">
                                            No se pudo guardar el PDF; los datos sí se han leído.
                                            Puedes crear la ficha y adjuntar el CV a mano.
                                        </Alert>
                                    )}
                                <Summary values={extracted}/>
                            </>
                        )}
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button variant="text" onClick={() => { reset(); setOpen(false); }} disabled={loading}>
                        Cancelar
                    </Button>
                    {extracted
                        ? (
                            <Button variant="filled" color="primary" onClick={openForm}>
                                Abrir formulario
                            </Button>
                        )
                        : (
                            <Button
                                variant="filled"
                                color="primary"
                                onClick={() => void extract()}
                                disabled={!file || loading}
                            >
                                Leer CV
                            </Button>
                        )}
                </DialogActions>
            </Dialog>
        </>
    );
}

export default ImportCvAction;
